/**
 * TEMPORARY HOOK
 *
 * This hook handles typeahead streaming submission logic for natural language search.
 * It is temporary and will be refactored once the typeahead streaming feature is fully implemented.
 */
import { useCallback, useId } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { streamTypeahead } from 'actions/SearchActions'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useRecommendationPlaceIds from './useRecommendationPlaceIds'

interface UseTypeaheadSubmitOptions {
  enabled: boolean
  typeaheadTypes?: Array<string>
  limit?: number
  rollout?: Array<string>
}

interface TypeaheadSubmitResult {
  /** Dispatch streaming extraction request on search submit (skips if extraction already completed) */
  dispatchTypeaheadExtraction: (refData: App.TypeaheadSubmitRefData | null, searchItem?: App.SearchItem) => void
  /** Prepare search item with typeaheadId for natural language searches */
  prepareSearchItem: (
    searchItem: App.SearchItem | undefined,
    fallbackItem: App.SearchItem | undefined,
    refData: App.TypeaheadSubmitRefData | null,
  ) => App.SearchItem | undefined
  /** Listener ID for the streaming request */
  listenerId: string
}

/**
 * Hook to handle typeahead streaming logic on search submission.
 * Consolidates duplicate logic between desktop SearchForm and mobile useGlobalSearchVerticalTabBooking.
 */
export function useTypeaheadSubmit(options: UseTypeaheadSubmitOptions): TypeaheadSubmitResult {
  const { enabled, typeaheadTypes, limit = 9, rollout } = options

  const dispatch = useAppDispatch()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const recentPlaceIds = useRecommendationPlaceIds()
  const listenerId = useId()

  /**
   * Dispatch streaming extraction request when user submits search.
   * This triggers the backend to extract search parameters from the natural language query.
   * @param refData - Reference data with typeaheadId and searchPhrase
   * @param searchItem - Current search item (used to check if extraction is already complete)
   */
  const dispatchTypeaheadExtraction = useCallback((
    refData: App.TypeaheadSubmitRefData | null,
    searchItem?: App.SearchItem,
  ) => {
    if (
      !enabled ||
      !refData?.typeaheadId ||
      !refData?.searchPhrase ||
      searchItem?.searchType !== 'searchItemV3' ||
      searchItem.display.searchType !== 'naturalLanguage'
    ) {
      return
    }

    // Skip extraction if searchItem already has extractedParams (extraction already completed)
    if (!searchItem.searchItem.extracting) {
      return
    }

    // Cancel any pending debounced streaming requests to avoid duplicate calls
    refData.cancelDebounce?.()

    dispatch(
      streamTypeahead({
        input: refData.searchPhrase,
        typeaheadId: refData.typeaheadId,
        listenerId,
        types: typeaheadTypes,
        limit,
        isLuxPlusMember: canViewLuxPlusBenefits,
        temporaryDoNotExtract: false,
        rollout,
        recentPlaceIds,
      }),
    )
  }, [enabled, dispatch, listenerId, typeaheadTypes, limit, canViewLuxPlusBenefits, rollout, recentPlaceIds])

  /**
   * Prepare search item for natural language searches:
   * - Creates a natural language item if user typed but didn't select
   * - Attaches typeaheadId and extracting flag to natural language items
   * - Always sets extracting: true because extraction happens on submit
   */
  const prepareSearchItem = useCallback((
    searchItem: App.SearchItem | undefined,
    fallbackItem: App.SearchItem | undefined,
    refData: App.TypeaheadSubmitRefData | null,
  ): App.SearchItem | undefined => {
    if (!enabled) return searchItem ?? fallbackItem

    let selectedItem = searchItem ?? fallbackItem

    // If this is a natural language search, ensure typeaheadId is set from ref if not already present
    // Only set extracting: true if extractedParams doesn't exist (extraction not complete)
    if (
      selectedItem?.searchType === 'searchItemV3' &&
      selectedItem.display.searchType === 'naturalLanguage' &&
      !selectedItem.searchItem.typeaheadId &&
      refData?.typeaheadId
    ) {
      selectedItem = {
        ...selectedItem,
        searchItem: {
          ...selectedItem.searchItem,
          typeaheadId: refData.typeaheadId,
        },
      }
    }

    return selectedItem
  }, [enabled])

  return {
    dispatchTypeaheadExtraction,
    prepareSearchItem,
    listenerId,
  }
}
